import useAPI, { useAPIMutation } from "@/services/api/hook";
import { ExtendedUseQueryResult } from "@/services/api/types";
import {
  OnboardingStage,
  OnboardingStageWorkflow,
  OnboardingStageWorkflowItem,
  OnboardingStagesResponse,
  OnboardingWorkflowDetailsInput,
  OnboardingWorkflowItemInput,
  OnboardingWorkflowItemUpdateInput,
  OnboardingWorkflowsResponse
} from "@/pages/settings/components/contractsSettings/types";

export const useOnboardingStages =
  (): ExtendedUseQueryResult<OnboardingStagesResponse> =>
    useAPI("settings/contracts/stages");

export const useOnboardingStageById = (
  id: string,
  enabled = true
): ExtendedUseQueryResult<OnboardingStage> =>
  useAPI(
    "settings/contracts/stages/stageById",
    { pathParams: { stageId: id } },
    { enabled }
  );

export const useCreateOnboardingStage = () =>
  useAPIMutation<
    {
      data: {
        name: string;
        stageColor: string;
        description: string | null;
      };
    },
    OnboardingStage
  >("settings/contracts/stages", { method: "POST" });

export const useUpdateOnboardingStage = () =>
  useAPIMutation<
    {
      pathParams: { stageId: string };
      data: {
        name: string;
        stageColor: string;
        description: string | null;
      };
    },
    OnboardingStage
  >("settings/contracts/stages/stageById", { method: "PUT" });

export const useDeleteOnboardingStage = () =>
  useAPIMutation<
    {
      pathParams: { stageId: string };
      data?: { targetStageId: string | null };
    },
    void
  >("settings/contracts/stages/stageById", { method: "DELETE" });

export const useUpdateOnboardingStagesOrder = () =>
  useAPIMutation<{ data: string[] }, void>(
    "settings/contracts/stages/order",
    { method: "PUT" }
  );

export const useOnboardingWorkflowById = (
  id: string,
  enabled = true
): ExtendedUseQueryResult<OnboardingStageWorkflow> =>
  useAPI(
    "settings/contracts/stages/workflows/workflowById",
    { pathParams: { workflowId: id } },
    { enabled }
  );

export const useAllOnboardingWorkflows = (
  enabled = true
): ExtendedUseQueryResult<OnboardingWorkflowsResponse> =>
  useAPI("settings/contracts/stages/workflows/all", undefined, { enabled });

export const useAssignOnboardingWorkflowToStage = () =>
  useAPIMutation<
    {
      pathParams: { workflowId: string };
      data: { contractsOnboardingStageId: string };
    },
    OnboardingStageWorkflow
  >("settings/contracts/stages/workflows/workflowById", { method: "PATCH" });

export const useCreateOnboardingWorkflow = () =>
  useAPIMutation<{ data: OnboardingWorkflowDetailsInput }, OnboardingStageWorkflow>(
    "settings/contracts/stages/workflows",
    { method: "POST" }
  );

export const useUpdateOnboardingWorkflow = () =>
  useAPIMutation<
    {
      pathParams: { workflowId: string };
      data: OnboardingWorkflowDetailsInput;
    },
    OnboardingStageWorkflow
  >("settings/contracts/stages/workflows/workflowById", { method: "PUT" });

export const useRemoveOnboardingWorkflowFromStage = () =>
  useAPIMutation<{ pathParams: { workflowId: string } }, void>(
    "settings/contracts/stages/workflows/workflowById",
    { method: "DELETE" }
  );

export const useUpdateOnboardingWorkflowsOrder = () =>
  useAPIMutation<{ data: string[] }, void>(
    "settings/contracts/stages/workflows/order",
    { method: "PUT" }
  );

export const useCreateOnboardingWorkflowItem = () =>
  useAPIMutation<
    { data: OnboardingWorkflowItemInput },
    OnboardingStageWorkflowItem
  >("settings/contracts/stages/workflows/items", { method: "POST" });

export const useUpdateOnboardingWorkflowItem = () =>
  useAPIMutation<
    {
      pathParams: { itemId: string };
      data: OnboardingWorkflowItemUpdateInput;
    },
    OnboardingStageWorkflowItem
  >("settings/contracts/stages/workflows/items/itemById", { method: "PUT" });

export const useDeleteOnboardingWorkflowItem = () =>
  useAPIMutation<{ pathParams: { itemId: string } }, void>(
    "settings/contracts/stages/workflows/items/itemById",
    { method: "DELETE" }
  );

// On demand, unlike the workflow read: an item read still returns drafts.
export const useFetchOnboardingWorkflowItem = () =>
  useAPIMutation<
    { pathParams: { itemId: string } },
    OnboardingStageWorkflowItem
  >("settings/contracts/stages/workflows/items/itemById", { method: "GET" });

export const useUpdateOnboardingWorkflowItemsOrder = () =>
  useAPIMutation<{ data: string[] }, void>(
    "settings/contracts/stages/workflows/items/order",
    { method: "PUT" }
  );
